import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Download, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// Spotlights one specific material on the Dashboard by title — update this
// to feature a different asset.
const FEATURED_TITLE = 'RAAH Technologies Reaches Apex';

// Phrases worth calling out in the description — update alongside the
// material's description if the featured asset changes.
const HIGHLIGHT_TERMS = [
  'Apex Partner',
  'Okta Elevate Partner Program',
  'L3 identity experts',
  'pre-sales',
  'in-flight',
  'post-sales',
];

function highlightText(text, accentStyle) {
  if (!text) return text;
  const pattern = new RegExp(`(${HIGHLIGHT_TERMS.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  return text.split(pattern).map((part, i) => (
    HIGHLIGHT_TERMS.includes(part)
      ? <strong key={i} style={accentStyle}>{part}</strong>
      : part
  ));
}

const styles = {
  hero: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'linear-gradient(135deg, #0a1e42 0%, #15306b 100%)',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '24px',
    color: '#fff',
  },
  left: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  iconWrap: {
    width: '40px', height: '40px', borderRadius: '10px',
    background: 'rgba(255,255,255,0.15)', color: '#fff', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    marginTop: '2px',
  },
  eyebrow: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8fb3ff' },
  title: { fontSize: '16px', fontWeight: '700', marginTop: '2px', color: '#fff' },
  description: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '6px', maxWidth: '640px', lineHeight: 1.5 },
  highlight: { color: '#8fb3ff', fontWeight: '700' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' },
  meta: { fontSize: '12px', color: 'rgba(255,255,255,0.6)' },
  btn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '10px 22px', background: '#fff', color: '#15306b',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 'auto',
    boxShadow: '0 2px 10px rgba(0,0,0,0.25)', transition: 'transform 0.1s ease',
  },
};

function FeaturedMaterialHero() {
  const { getAccessTokenSilently } = useAuth0();
  const [material, setMaterial] = useState(null);

  const getToken = async () => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const params = new URLSearchParams({ search: FEATURED_TITLE });
        const res = await fetch(`${API_URL}api/materials?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const match = (data.materials || []).find(m => m.title === FEATURED_TITLE) || null;
        setMaterial(match);
      } catch (err) {
        console.error('Failed to load featured material:', err);
      }
    })();
  }, []);

  const handleDownload = async () => {
    if (!material) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}api/materials/${material._id}/download`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  if (!material) return null;

  return (
    <div style={styles.hero}>
      <div style={styles.left}>
        <div style={styles.iconWrap}><Sparkles size={20} /></div>
        <div>
          <div style={styles.eyebrow}>Current News</div>
          <div style={styles.title}>{material.title}</div>
          {material.description && (
            <div style={styles.description}>{highlightText(material.description, styles.highlight)}</div>
          )}
        </div>
      </div>
      <div style={styles.footer}>
        <div style={styles.meta}>{[material.type, material.product, material.industry].filter(Boolean).join(' · ')}</div>
        <button
          style={styles.btn}
          onClick={handleDownload}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <Download size={14} /> Download
        </button>
      </div>
    </div>
  );
}

export default FeaturedMaterialHero;
